// frontend/src/components/common/ui/Pagination.tsx
// Unified pagination component for consistent pagination across the application

import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

// Utility function to combine class names
const cn = (...classes: (string | undefined | null | boolean)[]): string => {
  return classes.filter(Boolean).join(' ');
};

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  showInfo?: boolean;
  showFirstLast?: boolean;
  maxVisiblePages?: number;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'simple' | 'numbered';
  className?: string;
  disabled?: boolean;
}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  startItem: number;
  endItem: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// Hook to calculate pagination info
export const usePaginationInfo = (
  currentPage: number,
  totalPages: number,
  totalItems: number,
  itemsPerPage: number
): PaginationInfo => {
  const startItem = ((currentPage - 1) * itemsPerPage) + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  const hasNext = currentPage < totalPages;
  const hasPrevious = currentPage > 1;

  return {
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    startItem,
    endItem,
    hasNext,
    hasPrevious,
  };
};

// Generate page numbers for display
const generatePageNumbers = (
  currentPage: number,
  totalPages: number,
  maxVisiblePages: number = 5
): (number | string)[] => {
  if (totalPages <= maxVisiblePages) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  const halfVisible = Math.floor(maxVisiblePages / 2);

  if (currentPage <= halfVisible + 1) {
    // Show first pages
    for (let i = 1; i <= maxVisiblePages - 1; i++) {
      pages.push(i);
    }
    pages.push('...');
    pages.push(totalPages);
  } else if (currentPage >= totalPages - halfVisible) {
    // Show last pages
    pages.push(1);
    pages.push('...');
    for (let i = totalPages - (maxVisiblePages - 2); i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    // Show middle pages
    pages.push(1);
    pages.push('...');
    for (let i = currentPage - halfVisible + 1; i <= currentPage + halfVisible - 1; i++) {
      pages.push(i);
    }
    pages.push('...');
    pages.push(totalPages);
  }

  return pages;
};

// Size configurations
const sizeConfig = {
  sm: {
    button: 'px-2 py-1 text-xs',
    container: 'gap-1',
    text: 'text-xs',
  },
  md: {
    button: 'px-3 py-2 text-sm',
    container: 'gap-2',
    text: 'text-sm',
  },
  lg: {
    button: 'px-4 py-2 text-base',
    container: 'gap-3',
    text: 'text-base',
  },
};

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  showInfo = true,
  showFirstLast = true,
  maxVisiblePages = 5,
  size = 'md',
  variant = 'default',
  className,
  disabled = false,
}) => {
  const paginationInfo = usePaginationInfo(currentPage, totalPages, totalItems, itemsPerPage);
  
  if (totalPages <= 1) return null;

  const baseButtonClass = cn(
    'relative inline-flex items-center justify-center border font-medium transition-colors duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    sizeConfig[size].button
  );

  const primaryButtonClass = cn(
    baseButtonClass,
    'border-gray-300 text-gray-700 bg-white hover:bg-gray-50',
    'disabled:hover:bg-white'
  );

  const currentButtonClass = cn(
    baseButtonClass,
    'border-blue-500 text-white bg-blue-600 hover:bg-blue-700',
    'disabled:hover:bg-blue-600'
  );

  const handlePageChange = (page: number) => {
    if (disabled || page < 1 || page > totalPages || page === currentPage) return;
    onPageChange(page);
  };

  const renderSimplePagination = () => (
    <div className={cn('flex items-center justify-between', className)}>
      {showInfo && (
        <div className={cn('text-gray-700', sizeConfig[size].text)}>
          Showing {paginationInfo.startItem} to {paginationInfo.endItem} of {totalItems} results
        </div>
      )}
      
      <div className={cn('flex items-center', sizeConfig[size].container)}>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={disabled || !paginationInfo.hasPrevious}
          className={cn(primaryButtonClass, 'rounded-l-md')}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </button>
        
        <span className={cn('px-4 text-gray-700', sizeConfig[size].text)}>
          Page {currentPage} of {totalPages}
        </span>
        
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={disabled || !paginationInfo.hasNext}
          className={cn(primaryButtonClass, 'rounded-r-md')}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </button>
      </div>
    </div>
  );

  const renderNumberedPagination = () => {
    const pageNumbers = generatePageNumbers(currentPage, totalPages, maxVisiblePages);
    
    return (
      <div className={cn('flex items-center justify-between', className)}>
        {showInfo && (
          <div className={cn('text-gray-700', sizeConfig[size].text)}>
            Showing {paginationInfo.startItem} to {paginationInfo.endItem} of {totalItems} results
          </div>
        )}
        
        <div className={cn('flex items-center', sizeConfig[size].container)}>
          {showFirstLast && (
            <button
              onClick={() => handlePageChange(1)}
              disabled={disabled || currentPage === 1}
              className={cn(primaryButtonClass, 'rounded-md mr-1')}
              title="First page"
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
          )}
          
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={disabled || !paginationInfo.hasPrevious}
            className={cn(primaryButtonClass, 'rounded-md')}
            title="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          
          <div className={cn('flex items-center', sizeConfig[size].container)}>
            {pageNumbers.map((page, index) => (
              <React.Fragment key={index}>
                {page === '...' ? (
                  <span className={cn('px-3 py-2 text-gray-500', sizeConfig[size].text)}>
                    ...
                  </span>
                ) : (
                  <button
                    onClick={() => handlePageChange(page as number)}
                    disabled={disabled}
                    className={cn(
                      page === currentPage ? currentButtonClass : primaryButtonClass,
                      'rounded-md min-w-[2.5rem]'
                    )}
                  >
                    {page}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={disabled || !paginationInfo.hasNext}
            className={cn(primaryButtonClass, 'rounded-md')}
            title="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          
          {showFirstLast && (
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={disabled || currentPage === totalPages}
              className={cn(primaryButtonClass, 'rounded-md ml-1')}
              title="Last page"
            >
              <ChevronsRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderDefaultPagination = () => (
    <div className={cn('bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6', className)}>
      <div className="flex-1 flex justify-between items-center">
        {showInfo && (
          <div className={cn('text-gray-700', sizeConfig[size].text)}>
            Showing {paginationInfo.startItem} to {paginationInfo.endItem} of {totalItems} results
          </div>
        )}
        
        <div className={cn('flex items-center', sizeConfig[size].container)}>
          {showFirstLast && totalPages > 5 && (
            <button
              onClick={() => handlePageChange(1)}
              disabled={disabled || currentPage === 1}
              className={cn(primaryButtonClass, 'rounded-md mr-1')}
              title="First page"
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
          )}
          
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={disabled || !paginationInfo.hasPrevious}
            className={cn(primaryButtonClass, 'rounded-md')}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </button>
          
          <span className={cn('px-4 text-gray-700', sizeConfig[size].text)}>
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={disabled || !paginationInfo.hasNext}
            className={cn(primaryButtonClass, 'rounded-md')}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
          
          {showFirstLast && totalPages > 5 && (
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={disabled || currentPage === totalPages}
              className={cn(primaryButtonClass, 'rounded-md ml-1')}
              title="Last page"
            >
              <ChevronsRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  switch (variant) {
    case 'simple':
      return renderSimplePagination();
    case 'numbered':
      return renderNumberedPagination();
    default:
      return renderDefaultPagination();
  }
};

// Pagination info component
interface PaginationInfoProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  className?: string;
}

export const PaginationInfo: React.FC<PaginationInfoProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  className,
}) => {
  const info = usePaginationInfo(currentPage, totalPages, totalItems, itemsPerPage);
  
  return (
    <div className={cn('text-sm text-gray-700', className)}>
      Showing {info.startItem} to {info.endItem} of {totalItems} results
    </div>
  );
};

// Mobile pagination component
interface MobilePaginationProps extends Omit<PaginationProps, 'variant'> {
  label?: string;
}

export const MobilePagination: React.FC<MobilePaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  showInfo = true,
  size = 'md',
  className,
  disabled = false,
  label = 'Page',
}) => {
  const paginationInfo = usePaginationInfo(currentPage, totalPages, totalItems, itemsPerPage);
  
  if (totalPages <= 1) return null;

  const handlePageChange = (page: number) => {
    if (disabled || page < 1 || page > totalPages || page === currentPage) return;
    onPageChange(page);
  };

  return (
    <div className={cn('flex flex-col space-y-3 sm:hidden', className)}>
      {showInfo && (
        <PaginationInfo
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          className="text-center"
        />
      )}
      
      <div className="flex items-center justify-between">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={disabled || !paginationInfo.hasPrevious}
          className={cn(
            'relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </button>
        
        <span className="text-sm text-gray-700">
          {label} {currentPage} of {totalPages}
        </span>
        
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={disabled || !paginationInfo.hasNext}
          className={cn(
            'relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </button>
      </div>
    </div>
  );
};

// Per page selector component
interface PerPageSelectorProps {
  itemsPerPage: number;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  options?: number[];
  label?: string;
  className?: string;
  disabled?: boolean;
}

export const PerPageSelector: React.FC<PerPageSelectorProps> = ({
  itemsPerPage,
  onItemsPerPageChange,
  options = [10, 25, 50, 100],
  label = 'Show',
  className,
  disabled = false,
}) => {
  return (
    <div className={cn('flex items-center space-x-2', className)}>
      <label className="text-sm text-gray-700">{label}</label>
      <select
        value={itemsPerPage}
        onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
        disabled={disabled}
        className={cn(
          'border border-gray-300 rounded-md px-3 py-1 text-sm',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
          'disabled:opacity-50 disabled:cursor-not-allowed'
        )}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <span className="text-sm text-gray-700">per page</span>
    </div>
  );
};

// Combined pagination with per page selector
interface PaginationWithSelectorProps extends PaginationProps {
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  perPageOptions?: number[];
  showPerPageSelector?: boolean;
}

export const PaginationWithSelector: React.FC<PaginationWithSelectorProps> = ({
  onItemsPerPageChange,
  perPageOptions,
  showPerPageSelector = true,
  ...paginationProps
}) => {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
      {showPerPageSelector && onItemsPerPageChange && (
        <PerPageSelector
          itemsPerPage={paginationProps.itemsPerPage}
          onItemsPerPageChange={onItemsPerPageChange}
          options={perPageOptions}
          disabled={paginationProps.disabled}
        />
      )}
      
      <Pagination {...paginationProps} showInfo={false} />
    </div>
  );
};

export default Pagination;